import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import API from "../utils/api";
import { formatPhoneDisplay } from "../utils/formatPhone";

function money(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `$${amount.toFixed(2)}`;
}

export default function ProposalCustomerPage() {
  const { proposalId } = useParams();
  const [proposal, setProposal] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProposal(null);
    setMissing(false);
    API.get(`proposals/${proposalId}/customer/`)
      .then((response) => {
        if (!cancelled) setProposal(response.data);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  if (missing) {
    return (
      <main style={pageStyle}>
        <h1 style={{ fontSize: 28, marginBottom: 12 }}>This proposal is not available</h1>
        <p style={{ color: "#475569", lineHeight: 1.5 }}>
          The link may be out of date. Contact your representative if you need a new copy.
        </p>
      </main>
    );
  }

  if (!proposal) {
    return (
      <main style={pageStyle}>
        <p style={{ color: "#64748b" }}>Loading proposal…</p>
      </main>
    );
  }

  const es = proposal.language === "es";
  const canRespond = proposal.status === "sent";
  const discount = Number(proposal.discount || 0);
  const statusNote =
    proposal.status === "accepted"
      ? es
        ? "Usted aceptó esta propuesta."
        : "You accepted this proposal."
      : proposal.status === "declined"
        ? es
          ? "Esta propuesta fue rechazada."
          : "This proposal was declined."
        : proposal.status === "withdrawn"
          ? es
            ? "Esta propuesta ya no está disponible."
            : "This proposal is no longer open."
          : "";
  const greetingName = proposal.greeting_name || (es ? "" : "there");
  const greeting = es
    ? greetingName
      ? `Hola ${greetingName}, gracias por su interés. Con gusto le compartimos esta propuesta para su consideración.`
      : "Hola, gracias por su interés. Con gusto le compartimos esta propuesta para su consideración."
    : `Hi ${greetingName}, thank you for your interest — we're pleased to share this proposal for your consideration.`;

  return (
    <main style={pageStyle}>
      <p style={{ margin: "0 0 8px", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", fontWeight: 700 }}>
        {proposal.company_name || (es ? "Propuesta" : "Proposal")}
      </p>
      {proposal.valid_through ? (
        <p style={{ margin: "0 0 20px", color: "#64748b" }}>
          {es ? "Válida hasta" : "Valid through"} {proposal.valid_through}
        </p>
      ) : null}
      <p style={{ margin: "0 0 4px", fontSize: 13, color: "#64748b" }}>{es ? "Preparada para" : "Prepared for"}</p>
      <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.2 }}>
        {proposal.prepared_for_name || proposal.greeting_name || (es ? "usted" : "you")}
      </h1>
      {proposal.address ? (
        <p style={{ margin: "6px 0 0", fontSize: 16, fontWeight: 500, color: "#64748b", lineHeight: 1.4 }}>
          {proposal.address}
        </p>
      ) : null}
      <p style={{ margin: "18px 0 20px", fontSize: 16, color: "#475569", lineHeight: 1.65 }}>
        {greeting}
      </p>
      {statusNote ? (
        <p style={noteStyle}>{statusNote}</p>
      ) : null}
      {proposal.message ? (
        <p style={{ whiteSpace: "pre-line", lineHeight: 1.55, color: "#334155" }}>{proposal.message}</p>
      ) : null}

      <section style={cardStyle}>
        {(proposal.items || []).map((item, index) => (
          <div key={`${item.name}-${index}`} style={rowStyle}>
            <div>
              <div style={{ fontWeight: 650 }}>{item.name}</div>
              <div style={{ color: "#64748b", fontSize: 13 }}>
                {item.qty} × {money(item.unit_price)}
              </div>
            </div>
            <div style={{ fontWeight: 700 }}>{money(item.line_total)}</div>
          </div>
        ))}
        <div style={rowStyle}>
          <span>{es ? "Subtotal de equipo" : "Equipment Subtotal"}</span>
          <strong>{money(proposal.subtotal)}</strong>
        </div>
        {discount > 0 ? (
          <div style={rowStyle}>
            <span>{proposal.discount_label || "Discount"}</span>
            <strong style={{ color: "#b91c1c" }}>−{money(discount)}</strong>
          </div>
        ) : null}
        {proposal.activation_fee != null && Number(proposal.activation_fee) > 0 ? (
          <div style={rowStyle}>
            <span>{es ? "Cargo de activación de ADT" : "ADT Activation Fee"}</span>
            <strong>{money(proposal.activation_fee)}</strong>
          </div>
        ) : null}
        <div style={{ ...rowStyle, borderTop: "2px solid #e2e8f0", paddingTop: 12 }}>
          <strong>{es ? "Total a pagar (único)" : "Total due (one-time)"}</strong>
          <strong>{money(proposal.total)}</strong>
        </div>
      </section>

      {proposal.monthly_service_charge != null ? (
        <section style={cardStyle}>
          <div style={rowStyle}>
            <div style={{ fontWeight: 700 }}>{proposal.monthly_service_label || "Monthly service"}</div>
            <strong>{money(proposal.monthly_service_charge)}{es ? " / mes" : " / mo"}</strong>
          </div>
          {proposal.monitoring_addon_name && proposal.monitoring_addon_charge != null ? (
            <>
              <div style={rowStyle}>
                <div style={{ fontWeight: 700 }}>{proposal.monitoring_addon_name}</div>
                <strong>{money(proposal.monitoring_addon_charge)}{es ? " / mes" : " / mo"}</strong>
              </div>
              <div style={rowStyle}>
                <div style={{ fontWeight: 700 }}>{es ? "Total de monitoreo mensual" : "Total Monthly Monitoring"}</div>
                <strong>
                  {money(Number(proposal.monthly_service_charge) + Number(proposal.monitoring_addon_charge))}{es ? " / mes" : " / mo"}
                </strong>
              </div>
            </>
          ) : null}
          <div style={{ color: "#64748b", fontSize: 13, marginTop: 8 }}>
            {es
              ? "Suscripción mensual recurrente. Contrato de 36 meses."
              : "Recurring monthly subscription. 36 Month Agreement."}
          </div>
        </section>
      ) : null}

      {proposal.special_terms ? (
        <section style={{ ...cardStyle, borderLeft: "3px solid #2563eb" }}>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", fontWeight: 700, marginBottom: 6 }}>
            {es ? "Términos especiales" : "Special terms"}
          </div>
          <div style={{ whiteSpace: "pre-line", lineHeight: 1.55, color: "#334155" }}>{proposal.special_terms}</div>
        </section>
      ) : null}

      {canRespond ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
          <a href={proposal.accept_url} style={acceptStyle}>{es ? "Aceptar propuesta" : "Accept proposal"}</a>
          <Link to={`/proposal/decline/${proposal.lead_id}`} style={declineStyle}>{es ? "Rechazar" : "Decline"}</Link>
        </div>
      ) : null}

      <footer style={{ marginTop: 28, paddingTop: 16, borderTop: "1px solid #e2e8f0" }}>
        <p style={{ margin: "0 0 4px", color: "#64748b" }}>{es ? "Con respeto," : "With respect,"}</p>
        <p style={{ margin: 0, fontWeight: 700 }}>{proposal.signoff_display_name}</p>
        {proposal.signoff_phone ? <p style={{ margin: "4px 0 0" }}>{formatPhoneDisplay(proposal.signoff_phone)}</p> : null}
        {proposal.signoff_email ? (
          <p style={{ margin: "4px 0 0" }}>
            <a href={`mailto:${proposal.signoff_email}`} style={{ color: "#2563eb" }}>{proposal.signoff_email}</a>
          </p>
        ) : null}
      </footer>
    </main>
  );
}

const pageStyle = {
  maxWidth: 640,
  margin: "0 auto",
  padding: "28px 20px 48px",
  color: "#0f172a",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const cardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: "4px 16px",
  marginBottom: 16,
  background: "#fff",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  alignItems: "center",
  padding: "12px 0",
  borderTop: "1px solid #f1f5f9",
};

const noteStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "12px 14px",
  marginBottom: 16,
};

const acceptStyle = {
  display: "inline-block",
  padding: "12px 22px",
  borderRadius: 999,
  background: "#16a34a",
  color: "#fff",
  textDecoration: "none",
  fontWeight: 700,
};

const declineStyle = {
  display: "inline-block",
  padding: "12px 22px",
  borderRadius: 999,
  background: "#fff",
  color: "#64748b",
  textDecoration: "none",
  fontWeight: 700,
  border: "1px solid #e2e8f0",
};
